import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  Input,
  Select,
  Radio,
  Button,
  Space,
  Typography,
  message,
  Alert,
  Card,
} from 'antd';
import {
  MessageOutlined,
  SaveOutlined,
  CloseOutlined,
} from '@ant-design/icons';
import api from '../../services/authApi';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

interface QuoteRequestFormProps {
  visible: boolean;
  customerId?: number;
  onClose: () => void;
  onSuccess?: () => void;
}

const QuoteRequestForm: React.FC<QuoteRequestFormProps> = ({
  visible,
  customerId,
  onClose,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [vehicles, setVehicles] = useState<any[]>([]);

  useEffect(() => {
    if (visible && customerId) {
      fetchVehicles();
      form.resetFields();
    }
  }, [visible, customerId, form]);

  const fetchVehicles = async () => {
    if (!customerId) return;

    try {
      const response = await api.get(`/admin/customers/${customerId}/vehicles`);
      setVehicles(response.data);
    } catch (error) {
      console.error('Error fetching vehicles:', error);
    }
  };

  const handleSubmit = async () => {
    if (!customerId) {
      message.error('Please log in to submit a quote request');
      return;
    }

    try {
      await form.validateFields();
      const values = form.getFieldsValue();

      setLoading(true);

      const quoteRequestData = {
        customer_id: customerId,
        vehicle_id: values.vehicle_id || null,
        service_type: values.service_type,
        description: values.description,
        urgency: values.urgency || 'normal',
        customer_notes: values.customer_notes || null,
        preferred_contact_time: values.preferred_contact_time || null,
      };

      await api.post(
        '/quote-requests/public',
        quoteRequestData
      );

      message.success('Quote request submitted successfully! We\'ll get back to you soon.');
      form.resetFields();
      if (onSuccess) onSuccess();
      onClose();
    } catch (error: any) {
      console.error('Error submitting quote request:', error);
      const errorMessage = error?.response?.data?.error || 'Failed to submit quote request';
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    form.resetFields();
    onClose();
  };

  return (
    <Modal
      title={
        <Space>
          <MessageOutlined />
          <Title level={4} style={{ margin: 0 }}>Request a Quote</Title>
        </Space>
      }
      open={visible}
      onCancel={handleClose}
      width={700}
      footer={[
        <Button key="cancel" onClick={handleClose} icon={<CloseOutlined />}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          loading={loading}
          onClick={handleSubmit}
          icon={<SaveOutlined />}
        >
          Submit Request
        </Button>,
      ]}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          urgency: 'normal',
        }}
      >
        <Alert
          message="Get a Custom Quote"
          description="Can't find what you're looking for in our service packages? Describe what you need and we'll create a custom quote for you."
          type="info"
          showIcon
          style={{ marginBottom: 24 }}
        />

        <Form.Item
          label="Service Type"
          name="service_type"
          rules={[{ required: true, message: 'Please enter the type of service you need' }]}
        >
          <Select
            placeholder="Select service type"
            showSearch
            allowClear
            options={[
              { value: 'Oil Change', label: 'Oil Change' },
              { value: 'Brake Service', label: 'Brake Service' },
              { value: 'Tire Service', label: 'Tire Service' },
              { value: 'Engine Diagnostics', label: 'Engine Diagnostics' },
              { value: 'Transmission Service', label: 'Transmission Service' },
              { value: 'Electrical Service', label: 'Electrical Service' },
              { value: 'AC/Heating Service', label: 'AC/Heating Service' },
              { value: 'Suspension Service', label: 'Suspension Service' },
              { value: 'Exhaust Service', label: 'Exhaust Service' },
              { value: 'Body Work', label: 'Body Work' },
              { value: 'Detailing', label: 'Detailing' },
              { value: 'Inspection', label: 'Inspection' },
              { value: 'Custom Work', label: 'Custom Work' },
              { value: 'Other', label: 'Other' },
            ]}
          />
        </Form.Item>

        <Form.Item
          label="Vehicle (Optional)"
          name="vehicle_id"
          help="Select the vehicle this service is for, if applicable"
        >
          <Select
            placeholder="Select a vehicle"
            allowClear
          >
            {vehicles.map(vehicle => (
              <Select.Option key={vehicle.id} value={vehicle.id}>
                {vehicle.year} {vehicle.make} {vehicle.model} {vehicle.vin && `(${vehicle.vin})`}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          label="Detailed Description"
          name="description"
          rules={[
            { required: true, message: 'Please provide a detailed description' },
            { min: 10, message: 'Please provide at least 10 characters' }
          ]}
        >
          <TextArea
            rows={5}
            placeholder="Please describe the service you need in detail. Include any symptoms, concerns, or specific requirements..."
            maxLength={1000}
            showCount
          />
        </Form.Item>

        <Form.Item
          label="Urgency"
          name="urgency"
          help="Let us know how soon you need this service"
        >
          <Radio.Group>
            <Radio.Button value="normal">Normal</Radio.Button>
            <Radio.Button value="urgent">Urgent (Within 1 Week)</Radio.Button>
            <Radio.Button value="emergency">Emergency (ASAP)</Radio.Button>
          </Radio.Group>
        </Form.Item>

        <Form.Item
          label="Additional Notes (Optional)"
          name="customer_notes"
        >
          <TextArea
            rows={3}
            placeholder="Any additional information or special requests..."
            maxLength={500}
            showCount
          />
        </Form.Item>

        <Form.Item
          label="Preferred Contact Time (Optional)"
          name="preferred_contact_time"
        >
          <Input
            placeholder="e.g., Weekday mornings, after 5pm, etc."
            maxLength={100}
          />
        </Form.Item>

        <Card size="small" style={{ backgroundColor: '#f0f5ff', borderColor: '#adc6ff' }}>
          <Space direction="vertical" size="small">
            <Text strong>What happens next?</Text>
            <Text type="secondary" style={{ fontSize: 12 }}>
              1. Our team will review your request
            </Text>
            <Text type="secondary" style={{ fontSize: 12 }}>
              2. We'll prepare a detailed quote with pricing
            </Text>
            <Text type="secondary" style={{ fontSize: 12 }}>
              3. You'll receive a notification when your quote is ready
            </Text>
            <Text type="secondary" style={{ fontSize: 12 }}>
              4. Review and approve to schedule your service
            </Text>
          </Space>
        </Card>
      </Form>
    </Modal>
  );
};

export default QuoteRequestForm;
